import { useState, Fragment } from 'react';
import Badge from './Badge';

/**
 * AccessTable - Shared expandable table component for access management
 * Used by both UserAccessTab and ProductAccessTab
 */
export default function AccessTable({
  columns,
  data,
  renderRow,
  renderExpandedDetails,
  getRowKey
}) {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleExpanded = (rowKey) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowKey)) {
        newSet.delete(rowKey);
      } else {
        newSet.add(rowKey);
      }
      return newSet;
    });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-700">
          <tr className="text-left text-gray-400">
            <th className="pb-3 pr-4 font-medium w-16"></th>
            {columns.map((column, idx) => (
              <th
                key={idx}
                className={column.className || 'pb-3 px-4 font-medium'}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {data.map((item) => {
            const rowKey = getRowKey(item);
            const isExpanded = expandedRows.has(rowKey);

            return (
              <Fragment key={rowKey}>
                <tr
                  className="hover:bg-gray-800/20 cursor-pointer"
                  onClick={() => toggleExpanded(rowKey)}
                >
                  <td className="py-3 pr-4">
                    <button
                      className="text-gray-400 hover:text-white transition-colors pointer-events-none"
                    >
                      <svg
                        className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </td>
                  {renderRow(item, isExpanded)}
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={columns.length + 1} className="py-0">
                      {renderExpandedDetails(item)}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Helper components for common access table patterns
 */

export function AccessSourceIcons({ accessReasons, getIcon, getLabel }) {
  return (
    <div className="flex items-center gap-1">
      {accessReasons.map((reason, idx) => (
        <span
          key={idx}
          className="text-base"
          title={getLabel(reason.source)}
        >
          {getIcon(reason.source)}
        </span>
      ))}
      <span className="text-xs text-gray-500 ml-1">
        ({accessReasons.length})
      </span>
    </div>
  );
}

export function AccessExpandedDetails({ accessReasons, getIcon, getLabel, getBadgeVariant }) {
  return (
    <div className="bg-gray-900/40 p-4 space-y-2">
      <div className="text-xs font-medium text-gray-400 mb-3">Access Details:</div>
      {accessReasons.map((reason, idx) => (
        <div
          key={idx}
          className="flex items-start gap-3 bg-gray-800/40 rounded p-2 border border-gray-700/30"
        >
          <span className="text-base flex-shrink-0 mt-0.5">
            {getIcon(reason.source)}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-gray-300">
                {getLabel(reason.source)}
              </span>
              <Badge
                variant={getBadgeVariant(reason.access_level)}
                className="text-xs"
              >
                {reason.access_level}
              </Badge>
            </div>
            <p className="text-xs text-gray-400">{reason.detail}</p>
            {(reason.granted_at || reason.expires_at) && (
              <div className="flex gap-3 mt-1">
                {reason.granted_at && (
                  <span className="text-xs text-gray-500">
                    Granted: {new Date(reason.granted_at).toLocaleDateString()}
                  </span>
                )}
                {reason.expires_at && (
                  <span className="text-xs text-yellow-400/80">
                    Expires: {new Date(reason.expires_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
